import { z } from 'zod'

/**
 * Zod Validation Schema for Upload Files
 *
 * This schema validates the FileType structure used across the application
 * for file uploads (both new uploads with signed_id and existing uploads with id).
 *
 * This uses a hybrid approach:
 * - FileType remains a TypeScript interface (used throughout the app)
 * - This Zod schema validates FileType properties at runtime (forms, API boundaries)
 * - Validates that uploads have EITHER id (existing) OR signed_id (new), not both
 *
 * Benefits:
 * 1. Runtime validation where needed (forms, API)
 * 2. Type inference for validated data
 * 3. Flexible refinement logic for upload state
 * 4. Compatible with existing FileType usage
 */

/**
 * Upload File Schema
 *
 * Validates FileType properties with the constraint that files must have
 * either 'id' (existing upload) OR 'signed_id' (new upload), but not both.
 *
 * NOTE: This schema is lenient to accommodate what Uploader.tsx actually produces:
 * - DirectUpload spreads blob properties (which vary)
 * - Not all File properties are always present
 * - Schema validates the essential upload state, not strict File compliance
 */
export const uploadFileSchema = z
  .object({
    // Native File properties (optional because Uploader doesn't always provide them)
    name: z.string().optional(),
    size: z.number().optional(),
    type: z.string().optional(),
    lastModified: z.number().optional(),

    // FileType extensions (from utils/fixtures.tsx)
    pageCount: z.number().optional(),
    createdAt: z.string().optional(),
    attachmentFilename: z.string().optional(),
    attachmentUrl: z.string().optional(),
    url: z.string().optional(),
    filename: z.string().optional(),
    path: z.string().optional(),

    // Upload state identifiers
    // Note: id can come as number from server, coerce to string
    id: z
      .union([z.string(), z.number()])
      .transform(val => String(val))
      .optional(),
    signed_id: z.string().optional(),

    // DirectUpload blob properties that might be present
    key: z.string().optional(),
    byte_size: z.number().optional(),
    content_type: z.string().optional(),
    checksum: z.string().optional()
  })
  .loose() // Allow additional properties from DirectUpload blob
  .refine(
    data => {
      // Must have at least one identifier (id OR signed_id)
      // Note: DirectUpload blobs can have BOTH (blob id + signed_id)
      // What matters: signed_id = new upload, id without signed_id = existing upload
      const hasId = !!data.id
      const hasSignedId = !!data.signed_id
      return hasId || hasSignedId // At least one must be present
    },
    {
      message: "File must have either 'id' or 'signed_id' to identify the upload"
    }
  )

/**
 * Type inference from schema
 * This type is compatible with FileType but enforces the id/signed_id constraint
 */
export type UploadFile = z.infer<typeof uploadFileSchema>

/**
 * Helper type guards for discriminating upload states
 */
export const isNewUpload = (file: UploadFile): file is UploadFile & { signed_id: string } => {
  return 'signed_id' in file && !!file.signed_id
}

export const isExistingUpload = (file: UploadFile): file is UploadFile & { id: string } => {
  return 'id' in file && !!file.id && !('signed_id' in file && file.signed_id)
}

/**
 * Server Upload type from Apollo Query responses
 */
export type ServerUpload = {
  id?: string | null
  attachmentUrl?: string | null
  attachmentFilename?: string | null
}

/**
 * Converts server uploads (from Apollo queries) to UploadFile format
 * Used when loading existing uploads into forms for editing
 *
 * Note: Creates a plain object (not File instance) because:
 * 1. File class has no index signature, causing TS errors with Object.assign
 * 2. We only need the metadata for display/diffing, not actual file content
 */
export const createUploadFileFromServer = (serverUpload: ServerUpload): UploadFile => {
  if (!serverUpload.id) {
    throw new Error('Server upload must have an id')
  }

  return {
    // Native File properties (for compatibility)
    name: serverUpload.attachmentFilename || 'Unknown File',
    type: 'application/octet-stream',
    lastModified: Date.now(),

    // Server properties
    id: serverUpload.id,
    attachmentUrl: serverUpload.attachmentUrl ?? undefined,
    attachmentFilename: serverUpload.attachmentFilename ?? undefined,

    // Display properties
    url: serverUpload.attachmentUrl ?? undefined,
    filename: serverUpload.attachmentFilename ?? undefined,

    // Optional properties (not available from server)
    pageCount: undefined,
    createdAt: undefined,
    path: undefined
  }
}
